import { ApiError, api } from './api.js';
import { downscale, postWithProgress } from './vehicles.js';

const enc = encodeURIComponent;
const ws = (vid) => `/v1/workshops/mine/vehicles/${enc(vid)}/maintenances`;

/** Manutenções: a oficina registra/corrige; o dono (e quem tem acesso) consulta. */
export const maintenances = {
  /** Tipos de serviço; com a linha do veículo, só os que valem para ela (e o intervalo dela em intervalMeter). */
  types: (category) => api('GET', `/v1/maintenance-types${category ? `?category=${encodeURIComponent(category)}` : ''}`),
  // oficina (autorização ativa)
  workshopList: (vid) => api('GET', ws(vid)),
  create: (vid, body) => api('POST', ws(vid), body),
  correct: (vid, id, body) => api('PUT', `${ws(vid)}/${enc(id)}`, body),
  workshopVersions: (vid, id) => api('GET', `${ws(vid)}/${enc(id)}/versions`),
  // dono
  ownerList: (vid) => api('GET', `/v1/vehicles/${enc(vid)}/maintenances`),
  ownerVersions: (vid, id) => api('GET', `/v1/vehicles/${enc(vid)}/maintenances/${enc(id)}/versions`),
  // dados iniciais do dono (ativar lembretes)
  initialGet: (vid) => api('GET', `/v1/vehicles/${enc(vid)}/initial-data`),
  initialCreate: (vid, body) => api('POST', `/v1/vehicles/${enc(vid)}/initial-data`, body),
  initialCorrect: (vid, body) => api('PUT', `/v1/vehicles/${enc(vid)}/initial-data`, body),
  updateKm: (vid, km) => api('POST', `/v1/vehicles/${enc(vid)}/km`, { km }),
};

/** Fotos por item e regra de foto de cada tipo (definida no admin). */
export const MAX_ITEM_PHOTOS = 3;
export const PHOTO_POLICY = {
  REQUIRED: { label: 'obrigatória', hint: 'Fotografe a peça nova e a velha lado a lado.' },
  OPTIONAL: { label: 'opcional', hint: 'Se tiver, fotografe a peça ou a embalagem.' },
  NONE: null,
};

/**
 * Foto da peça: reduz para JPEG (até 1600 px, ~300 KB), pede o POST assinado e envia direto para o S3.
 * Devolve o id da foto, que vai em items[].photos ao salvar a manutenção.
 * @param {string} vid
 * @param {File} file
 * @param {(fraction: number) => void} [onProgress]
 */
export async function uploadMaintenancePhoto(vid, file, onProgress = () => {}) {
  if (!file.type.startsWith('image/')) throw new ApiError(0, 'INVALID_FILE', 'Tire uma foto da peça.');
  let blob;
  try {
    blob = await downscale(file, 1600, 0.8);
  } catch {
    throw new ApiError(0, 'INVALID_FILE', 'Não foi possível ler esta foto. Tire de novo.');
  }
  const { photo, upload } = await api('POST', `/v1/workshops/mine/vehicles/${enc(vid)}/maintenance-photos`, { contentType: 'image/jpeg' });
  const form = new FormData();
  for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
  form.append('file', blob, 'peca.jpg'); // "file" precisa ser o último campo
  await postWithProgress(upload.url, form, onProgress);
  return { id: photo.id, blob };
}

/** "Hoje" no calendário de São Paulo, igual ao backend (YYYY-MM-DD). */
export const todayBR = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export function addDays(day, n) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function addMonths(day, n) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}

/** "2026-09-26" → "26/09/2026" */
export const fmtDay = (day) => (day ? day.split('-').reverse().join('/') : '');
/** Valor de um campo adicional do tipo, como aparece na tela: 4.5 → "4,5". */
export const fieldText = (f) => (typeof f.value === 'number' ? f.value.toLocaleString('pt-BR') : String(f.value ?? ''));
/**
 * Medidor do veículo: km (carro, caminhão) ou horas no horímetro (máquina agrícola, meterUnit HOURS).
 * Os campos da API continuam se chamando km; a unidade vem do veículo.
 * Telas de UM veículo chamam setMeterUnit() ao carregar; listas com vários passam a unidade em cada chamada.
 */
let currentUnit = 'KM';
export const setMeterUnit = (unit) => (currentUnit = unit === 'HOURS' ? 'HOURS' : 'KM');
export const meterUnit = () => currentUnit;
/** 50000 → "50.000 km"; 2418 (HOURS) → "2.418 h" */
export const fmtKm = (km, unit = currentUnit) => (km === null || km === undefined ? '—' : `${Number(km).toLocaleString('pt-BR')} ${unit === 'HOURS' ? 'h' : 'km'}`);
/** "Km atual" / "Horímetro" */
export const meterLabel = (unit = currentUnit) => (unit === 'HOURS' ? 'Horímetro' : 'Km atual');
/** Para frases: "a km" / "o horímetro" */
export const meterThe = (unit = currentUnit) => (unit === 'HOURS' ? 'o horímetro' : 'a km');
export const meterShort = (unit = currentUnit) => (unit === 'HOURS' ? 'h' : 'km');

export const UPCOMING_STATUS = {
  OVERDUE: { label: 'Vencida', color: 'danger' },
  SOON: { label: 'Em breve', color: 'warning' },
  OK: { label: 'Em dia', color: 'success' },
};

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/** "2026-04-15" → "abr/2026" */
export const fmtMonth = (day) => (day ? `${MONTHS[Number(day.slice(5, 7)) - 1]}/${day.slice(0, 4)}` : '');

/** Ícone da pergunta dos dados iniciais (o admin escolhe a chave). */
export const OWNER_ICON = {
  oil: 'water-outline',
  filter: 'funnel-outline',
  belt: 'cog-outline',
  battery: 'battery-half-outline',
  tire: 'aperture-outline',
  brake: 'disc-outline',
  spark: 'flash-outline',
  coolant: 'thermometer-outline',
  wrench: 'construct-outline',
};

/** Respostas rápidas sobre a última troca (tocar já avança). */
export const OWNER_ANSWERS = [
  { key: 'LT_3M', label: 'Menos de 3 meses', text: 'há menos de 3 meses' },
  { key: 'M3_6', label: 'De 3 a 6 meses', text: 'de 3 a 6 meses atrás' },
  { key: 'M6_12', label: 'De 6 meses a 1 ano', text: 'de 6 meses a 1 ano atrás' },
  { key: 'Y1_2', label: 'De 1 a 2 anos', text: 'de 1 a 2 anos atrás' },
  { key: 'GT_2Y', label: 'Mais de 2 anos', text: 'há mais de 2 anos' },
  { key: 'UNKNOWN', label: 'Não lembro', text: 'não lembra' },
];

/** Como o dono respondeu, para o histórico: "abr/2026 · 52.000 km", "de 3 a 6 meses atrás", "não lembra". */
export function answerText(it) {
  if (it.answer === 'EXACT') return [fmtMonth(it.lastDate), it.lastKm !== null && it.lastKm !== undefined ? fmtKm(it.lastKm) : ''].filter(Boolean).join(' · ');
  return OWNER_ANSWERS.find((a) => a.key === it.answer)?.text ?? '';
}

/** Quando é a próxima: "em 62.000 km ou até 15/04/2027"; estimada pela faixa: "por volta de mar/2028". */
export function nextText(u) {
  const date = u.nextDueDate ? (u.estimated ? `por volta de ${fmtMonth(u.nextDueDate)}` : `até ${fmtDay(u.nextDueDate)}`) : '';
  return [u.nextDueKm !== null && u.nextDueKm !== undefined ? `em ${fmtKm(u.nextDueKm)}` : '', date].filter(Boolean).join(' ou ');
}
